import {
  DocumentContentBlock,
  GenericContentBlock,
  TaskContentBlock,
} from "../../ContentBlocksPreview";

// Function to extract context data from structured prompts
export function extractContextFromPrompt(content: string): {
  contextConfig?: Map<any, any>;
  preview?: string;
  dataKeyId?: string;
} | null {
  // Check if this looks like a structured prompt with context
  const hasContext =
    content.includes("<context>") && content.includes("<data-key-config>");
  const hasDataKeyId = content.includes("<data-key-id>");

  if (!hasContext || !hasDataKeyId) {
    return null;
  }

  // Extract data key ID
  const dataKeyRegex = /<data-key-id>\s*(.*?)\s*<\/data-key-id>/s;
  const dataKeyMatch = content.match(dataKeyRegex);
  const dataKeyId = dataKeyMatch ? dataKeyMatch[1].trim() : undefined;

  // Extract related data keys to build context config
  const relatedDataKeysRegex =
    /<related-data-keys>([\s\S]*?)<\/related-data-keys>/;
  const relatedDataKeysMatch = content.match(relatedDataKeysRegex);

  const contextConfig = new Map();
  if (relatedDataKeysMatch) {
    const relatedDataKeysContent = relatedDataKeysMatch[1];
    // Extract each data key value pair
    const dataKeyValueRegex = /<([^>]+)>([\s\S]*?)<\/\1>/g;
    let match;
    while ((match = dataKeyValueRegex.exec(relatedDataKeysContent)) !== null) {
      const key = match[1];
      const value = match[2];
      contextConfig.set(key, {
        complete: value !== "undefined" && value.trim().length > 0,
        value: value === "undefined" ? undefined : value,
      });
    }
  }

  // Generate a preview text
  const preview = dataKeyId
    ? `Suggestion for: ${dataKeyId}`
    : "System-generated prompt";

  return {
    contextConfig: contextConfig.size > 0 ? contextConfig : undefined,
    preview,
    dataKeyId,
  };
}

// Function to extract content from user-query tags
export function extractUserQueryContent(content: string): string | null {
  const userQueryRegex = /<user-query>([\s\S]*?)<\/user-query>/i;
  const match = content.match(userQueryRegex);
  return match ? match[1].trim() : null;
}

// Function to extract referenced documents from message content
export function extractReferencedDocuments(
  content: string
): Array<DocumentContentBlock | TaskContentBlock> {
  const blocks: Array<DocumentContentBlock | TaskContentBlock> = [];

  // Extract referenced documents
  const referencedDocsRegex =
    /<referenced-documents>([\s\S]*?)<\/referenced-documents>/;
  const referencedDocsMatch = content.match(referencedDocsRegex);

  if (referencedDocsMatch) {
    const documentsContent = referencedDocsMatch[1];
    const documentRegex =
      /<document>\s*<name>(.*?)<\/name>\s*<id>(.*?)<\/id>\s*(?:<device-id>(.*?)<\/device-id>\s*)?<\/document>/g;
    let match;

    while ((match = documentRegex.exec(documentsContent)) !== null) {
      const documentTitle = match[1].trim();
      const documentId = match[2].trim();
      const deviceId = match[3]?.trim();

      blocks.push({
        type: "document",
        documentId,
        documentTitle,
        ...(deviceId && { deviceId }),
        templateKey: "referenced-document",
        templateTitle: "Referenced Document",
        metadata: {
          documentId,
          documentTitle,
          ...(deviceId && { deviceId }),
          templateKey: "referenced-document",
          templateTitle: "Referenced Document",
        },
      });
    }
  }

  // Extract referenced data keys
  const referencedDataKeysRegex =
    /<referenced-data-keys>([\s\S]*?)<\/referenced-data-keys>/;
  const referencedDataKeysMatch = content.match(referencedDataKeysRegex);

  if (referencedDataKeysMatch) {
    const dataKeysContent = referencedDataKeysMatch[1];
    const dataKeyRegex =
      /<data-key>\s*([^<]*?)\s*(?:<device-id>(.*?)<\/device-id>\s*)?<\/data-key>/g;
    let match;

    while ((match = dataKeyRegex.exec(dataKeysContent)) !== null) {
      const dataKeyId = match[1].trim();
      const deviceId = match[2]?.trim();

      blocks.push({
        type: "document",
        documentId: `data-key-${dataKeyId}`,
        documentTitle: dataKeyId,
        ...(deviceId && { deviceId }),
        templateKey: "data-key",
        templateTitle: "Data Key",
        dataKeyId: dataKeyId as any,
        metadata: {
          documentId: `data-key-${dataKeyId}`,
          documentTitle: dataKeyId,
          ...(deviceId && { deviceId }),
          templateKey: "data-key",
          templateTitle: "Data Key",
          dataKeyId: dataKeyId as any,
        },
      });
    }
  }

  // Extract referenced tasks
  const referencedTasksRegex =
    /<referenced-tasks>([\s\S]*?)<\/referenced-tasks>/;
  const referencedTasksMatch = content.match(referencedTasksRegex);

  if (referencedTasksMatch) {
    const tasksContent = referencedTasksMatch[1];
    const taskRegex =
      /<task>\s*<name>(.*?)<\/name>\s*<task-state-id>(.*?)<\/task-state-id>\s*([\s\S]*?)<\/task>/g;
    let match;

    while ((match = taskRegex.exec(tasksContent)) !== null) {
      const taskTitle = match[1].trim();
      const taskStateId = match[2].trim();
      const taskBody = match[3] || "";
      const notesDocumentIdMatch = taskBody.match(
        /<notes-document-id>\s*(.*?)\s*<\/notes-document-id>/
      );
      const fileDocumentsMatch = taskBody.match(
        /<file-documents>([\s\S]*?)<\/file-documents>/
      );
      const fileDocumentIds: string[] = [];

      if (fileDocumentsMatch) {
        const fileDocumentsContent = fileDocumentsMatch[1];
        const documentIdRegex = /<document-id>\s*(.*?)\s*<\/document-id>/g;
        let documentMatch;
        while (
          (documentMatch = documentIdRegex.exec(fileDocumentsContent)) !== null
        ) {
          fileDocumentIds.push(documentMatch[1].trim());
        }
      }

      const notesDocumentId = notesDocumentIdMatch
        ? notesDocumentIdMatch[1].trim()
        : undefined;

      const taskBlock: TaskContentBlock = {
        type: "task",
        taskStateId,
        taskTitle,
        notesDocumentId,
        fileDocumentIds,
        metadata: {
          taskStateId,
          taskTitle,
          notesDocumentId,
          fileDocumentIds,
        },
      };

      blocks.push(taskBlock);
    }
  }

  return blocks;
}

export function extractAssistantProcessBlock(
  content: string
): GenericContentBlock | null {
  const assistantProcessRegex =
    /<change-process-state>[\s\S]*?<\/change-process-state>/i;
  if (!assistantProcessRegex.test(content)) {
    return null;
  }

  return {
    type: "generic",
    title: "Assistant Process",
    removable: false,
  };
}

export function isComplexValue(value: any): boolean {
  return Array.isArray(value) || (typeof value === "object" && value !== null);
}

export function keyToLabel(key?: string | null): string {
  if (!key || key.trim().length === 0) {
    return "";
  }

  return key
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

// Other examples: https://github.com/reduxjs/redux-devtools/tree/75322b15ee7ba03fddf10ac3399881e302848874/src/react/themes
export const JSON_TREE_THEME = {
  scheme: "grayscale",
  author: "alexandre gavioli (https://github.com/alexx2/)",
  base00: "#101010",
  base01: "#252525",
  base02: "#464646",
  base03: "#525252",
  base04: "#ababab",
  base05: "#b9b9b9",
  base06: "#e3e3e3",
  base07: "#f7f7f7",
  base08: "#7c7c7c",
  base09: "#999999",
  base0A: "#a0a0a0",
  base0B: "#8e8e8e",
  base0C: "#868686",
  base0D: "#686868",
  base0E: "#747474",
  base0F: "#5e5e5e",
};
